import React from 'react';

import {
  LogOut,
  Laptop,
  Bot,
  Terminal,
  Chrome,
  Radio
} from 'lucide-react';

import {
  usePrivacy
} from '../../contexts/PrivacyContext';

import {
  useRealtime
} from '../../contexts/RealtimeContext';

import {
  useAuth
} from '../../contexts/AuthContext';

import {
  PrivacyToggle
} from '../common/PrivacyToggle';


interface ServiceChipProps {
  label:
    string;

  active:
    boolean;

  icon:
    React.ReactNode;
}


const ServiceChip:
React.FC<ServiceChipProps> = ({
  label,
  active,
  icon
}) => {

  return (

    <div
      className={`
        inline-flex
        items-center
        gap-1.5
        h-7
        px-2.5
        rounded-lg
        border
        text-[10px]
        font-medium
        transition-colors
        ${
          active
            ? 'border-emerald-400/15 bg-emerald-400/[0.055] text-emerald-300'
            : 'border-white/[0.055] bg-white/[0.025] text-slate-500'
        }
      `}
    >

      <span
        className={
          active
            ? 'text-emerald-400'
            : 'text-slate-600'
        }
      >
        {icon}
      </span>

      <span>
        {label}
      </span>

    </div>
  );
};


export const Header:
React.FC = () => {

  const {
    isPrivacyMode,
    deviceLabel
  } =
    usePrivacy();


  const {
    deviceStatus,
    services,
    latencyMs
  } =
    useRealtime();


  const {
    user,
    signOut
  } =
    useAuth();


  const online =
    deviceStatus ===
      'online';


  return (

    <header
      className="
        relative
        z-50
        h-[62px]
        shrink-0
        px-3
        sm:px-4
        md:px-5
        flex
        items-center
        justify-between
        gap-3
        border-b
        border-white/[0.065]
        bg-slate-950/62
        backdrop-blur-2xl
        shadow-[0_12px_40px_rgba(2,6,23,0.16)]
      "
    >

      {/* --------------------------------------------------
          BRAND / HOST
      -------------------------------------------------- */}

      <div
        className="
          flex
          items-center
          gap-3
          min-w-0
        "
      >

        <div
          className="
            relative
            w-9
            h-9
            shrink-0
            rounded-xl
            border
            border-indigo-400/20
            bg-indigo-400/[0.075]
            flex
            items-center
            justify-center
            text-indigo-300
            font-bold
            text-sm
            shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_8px_24px_rgba(79,70,229,0.08)]
          "
        >

          K

          <span
            className={`
              absolute
              -right-1
              -bottom-1
              w-2.5
              h-2.5
              rounded-full
              border-2
              border-slate-950
              ${
                online
                  ? 'bg-emerald-400'
                  : 'bg-rose-400'
              }
            `}
          />

        </div>


        <div className="min-w-0">

          <div
            className="
              flex
              items-center
              gap-2
              min-w-0
            "
          >

            <h1
              className="
                truncate
                text-sm
                font-semibold
                tracking-tight
                text-slate-100
              "
            >

              {isPrivacyMode
                ? 'KAIZEN Workspace'
                : 'KAIZEN Remote'}

            </h1>


            <span
              className="
                hidden
                xl:inline-flex
                items-center
                px-1.5
                py-0.5
                rounded
                border
                border-white/[0.05]
                bg-white/[0.025]
                font-mono
                text-[8px]
                uppercase
                tracking-[0.12em]
                text-slate-600
              "
            >

              Control Plane

            </span>

          </div>


          <div
            className="
              flex
              items-center
              gap-1.5
              mt-0.5
              min-w-0
              text-[10px]
            "
          >

            <Radio
              className={`w-3 h-3 ${
                online
                  ? 'text-emerald-400'
                  : 'text-rose-400'
              }`}
            />


            <span
              className={
                online
                  ? 'text-emerald-300'
                  : 'text-slate-500'
              }
            >

              {online
                ? 'Legion Active'
                : 'Legion Offline'}

            </span>


            {latencyMs !== null && (

              <>

                <span className="text-slate-700">
                  ·
                </span>

                <span className="font-mono text-slate-500">
                  {latencyMs}ms
                </span>

              </>

            )}


            <span className="hidden md:inline text-slate-700">
              ·
            </span>


            <span className="hidden md:inline truncate text-slate-500">
              {deviceLabel}
            </span>

          </div>

        </div>

      </div>


      {/* --------------------------------------------------
          DESKTOP SERVICES
      -------------------------------------------------- */}

      <div
        className="
          hidden
          lg:flex
          items-center
          gap-1.5
          ml-auto
        "
      >

        <ServiceChip
          label="Agent"
          active={
            services.agent ===
              'connected'
          }
          icon={
            <Laptop className="w-3 h-3" />
          }
        />


        <ServiceChip
          label="PowerShell"
          active={
            services.powershell ===
              'ready'
          }
          icon={
            <Terminal className="w-3 h-3" />
          }
        />


        <ServiceChip
          label="Browser"
          active={
            services.chromeExtension ===
              'connected'
          }
          icon={
            <Chrome className="w-3 h-3" />
          }
        />


        <ServiceChip
          label="JARVIS"
          active={
            services.chatgptBridge ===
              'connected'
          }
          icon={
            <Bot className="w-3 h-3" />
          }
        />

      </div>


      {/* --------------------------------------------------
          OPERATOR
      -------------------------------------------------- */}

      <div
        className="
          flex
          items-center
          gap-2
          shrink-0
        "
      >

        <div className="hidden sm:block">
          <PrivacyToggle />
        </div>


        <div
          className="
            hidden
            md:flex
            w-8
            h-8
            rounded-lg
            border
            border-white/[0.065]
            bg-white/[0.035]
            items-center
            justify-center
            text-[10px]
            font-semibold
            text-slate-300
          "
          title={
            user?.email ||
            'Operator'
          }
        >

          {user?.email
            ?.charAt(0)
            .toUpperCase() ||
            'U'}

        </div>


        <button
          type="button"
          onClick={
            () =>
              signOut()
          }
          className="
            w-9
            h-9
            rounded-lg
            flex
            items-center
            justify-center
            text-slate-500
            hover:text-slate-200
            hover:bg-white/[0.045]
            transition-colors
          "
          title="Sign Out"
        >

          <LogOut className="w-4 h-4" />

        </button>

      </div>

    </header>
  );
};
